import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";

import brandDetailsCbt from "shared/cbt/api/brandsDetails";
import { getCbtToken } from "shared/data/customerTokens";
import { AppState } from "shared/data/reducers";
import brandsDetailsRequest, {
  checkErrorType,
} from "shared/gol-api/brandsDetails";
import createBrandDetailsData from "shared/lib/brandedFares/createBrandsData";
import createServiceNamesData from "shared/lib/brandedFares/createServiceNamesData";
import FlightOffer from "shared/types/flightOffer";

import BrandsTable from "@components/Flights/BrandedFares/BrandsTable";
import FormattedMessage from "@components/UI/FormattedMessage";
import LoaderMini from "@components/UI/LoaderMini";

import { getTripType } from "@lib/functions";

const Logger = require("shared/services/Logger");

interface IBrandsModal {
  flightOffer: FlightOffer;
  getRefsForFlightOffer: () => string[];
  currency: string;
  keepBookingClass: boolean | undefined;
}

const BrandsModal: React.FC<IBrandsModal> = ({
  getRefsForFlightOffer,
  currency,
  keepBookingClass,
}) => {
  const router = useRouter();
  const { searchQuery, cbtApiUrl, customerUsername, isCbt } = useSelector(
    (state: AppState) => ({
      cbtApiUrl:
        state.storage.frontendSettings?.dealerCorporateSettings?.cbtApiUrl,
      customerUsername: state.user.email,
      searchQuery: state.storage.searchQuery,
      isCbt: state.cbt.corporate !== null,
    })
  );

  const [brandsData, setBrandsData] = useState({
    loading: true,
    success: false,
    error: false,
    errorType: null,
    data: null,
    filteredServices: [],
    brandServiceNamesData: null,
  });

  const [brandsPolicies, setBrandsPolicies] = useState({
    loading: true,
    policies: null,
  });

  const filterUnknownServices = true;
  const filterNotIncludedServices = true;
  const filterMixedServices = true;

  const intl = useIntl();

  const tripType = getTripType(searchQuery);

  useEffect(() => {
    (async () => {
      const cbtToken = await getCbtToken();

      if (cbtToken) {
        const brandsPolicy = await brandDetailsCbt({
          cbtToken,
          customerUsername,
          cbtApiUrl,
          flightRefs: getRefsForFlightOffer(),
          costCenterId: searchQuery.costCenterId,
          guests: searchQuery.guests,
          travelReasonId: searchQuery.travelReasonId,
          userIds: searchQuery.userIds,
          tripType,
        });

        if (brandsPolicy?.data) {
          setBrandsPolicies({
            policies: brandsPolicy?.data,
            loading: false,
          });
        }
      } else {
        setBrandsPolicies({
          loading: false,
          policies: null,
        });
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cbtApiUrl, customerUsername]);

  useEffect(() => {
    const fetchData = async () => {
      if (brandsData.data) {
        return;
      }

      try {
        const data = await brandsDetailsRequest(
          getRefsForFlightOffer(),
          keepBookingClass,
          router.query?.currency as string
        );
        if (data.success) {
          const currentBrandsData = data.data.FlightOffers.FlightOffer[0];
          const {
            brandsDetailsData,
            filteredServiceNames,
          } = createBrandDetailsData(
            currentBrandsData,
            filterUnknownServices,
            filterNotIncludedServices,
            filterMixedServices
          );
          setBrandsData({
            data: brandsDetailsData,
            brandServiceNamesData: createServiceNamesData(
              data.codebook.BrandServiceNames.BrandServiceName,
              intl
            ),
            filteredServices: filteredServiceNames,
            loading: false,
            success: true,
            error: false,
          });
        } else {
          const errorType = checkErrorType(data);
          setBrandsData({
            loading: false,
            success: false,
            error: true,
            errorType,
          });
        }
      } catch (error) {
        setBrandsData({
          loading: false,
          success: false,
          error: true,
        });
        Logger.error(error);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (brandsData.loading || brandsPolicies.loading) {
    return (
      <div className="branded-fares -loading">
        <LoaderMini />
      </div>
    );
  }

  if (brandsData.success) {
    return (
      <div className="branded-fares -success">
        <BrandsTable
          brandsData={brandsData.data}
          brandServiceNames={brandsData.brandServiceNamesData}
          currency={currency}
          filteredServices={brandsData.filteredServices}
          brandsPolicies={brandsPolicies.policies}
          isCbt={isCbt}
        />
      </div>
    );
  }

  if (brandsData.error && brandsData.errorType === "NoAvailableSeatsError") {
    return (
      <div className="branded-fares -error">
        <FormattedMessage id="BrandedFares.BrandsDetailNoAvailableSeatsErrorMessage" />
      </div>
    );
  }

  if (brandsData.error) {
    return (
      <div className="branded-fares -error">
        <FormattedMessage id="BrandedFares.BrandsDetailErrorMessage" />
      </div>
    );
  }
};

export default BrandsModal;
